import useStaffData from "@app/data-hooks/use-staff-data.ts";
import useUserData from "@app/data-hooks/use-user-data.ts";
import { Button, DialogActions, DialogContent, DialogTitle, Stack } from "@mui/material";
import { useForm } from "@tanstack/react-form";
import { Delimiter, Dialog, PasswordField } from "bi-lib";

interface Props {
    id?: number;
    open: boolean;
    onClose: () => void;
}

interface FormProps {
    newPassword: string;
    newPasswordConfirm: string;
}

export default function StaffChangePassword({ id, open, onClose }: Props) {
    const { staff } = useStaffData({ id });
    const { adminChangePassword } = useUserData();

    const form = useForm({
        defaultValues: {} as FormProps,
        onSubmit: async ({ value }) => {
            await adminChangePassword.mutateAsync({ staff_id: id, new_password: value.newPassword });
            onClose();
        },
    });

    return (
        <Dialog open={open} onClose={onClose} maxWidth={"sm"} fullWidth>
            <DialogTitle>Reset {staff.data?.fullName}'s Password</DialogTitle>
            <form
                onSubmit={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    void form.handleSubmit();
                }}
            >
                <DialogContent>
                    <Stack spacing={1}>
                        <form.Field
                            name={"newPassword"}
                            validators={{
                                onChange: ({ value }) => {
                                    const errors: string[] = [];

                                    // Check length
                                    const minLength = 8;
                                    if (value.length < minLength) {
                                        errors.push(`Must be at least ${minLength} characters`);
                                    }

                                    // Check for lowercase
                                    if (!/[a-z]/.test(value)) {
                                        errors.push("Must contain at least 1 lowercase character");
                                    }

                                    // Check for uppercase
                                    if (!/[A-Z]/.test(value)) {
                                        errors.push("Must contain at least 1 uppercase character");
                                    }

                                    // Check for number
                                    if (!/[0-9]/.test(value)) {
                                        errors.push("Must contain at least 1 number");
                                    }

                                    // Check for number
                                    if (!/[!@#$%^&*(),.?":{}|<>]/.test(value)) {
                                        errors.push("Must contain at least 1 symbol");
                                    }

                                    return errors.length == 0 ? undefined : errors.join(Delimiter);
                                },
                            }}
                        >
                            {(field) => (
                                <PasswordField
                                    label={"New Password"}
                                    value={field.state.value}
                                    onChange={field.handleChange}
                                    required
                                    error={field.state.meta.errors.length > 0}
                                    helperText={field.state.meta.errors.join(Delimiter)}
                                />
                            )}
                        </form.Field>
                        <form.Field
                            name={"newPasswordConfirm"}
                            validators={{
                                onChangeListenTo: ["newPassword"],
                                onChange: ({ value, fieldApi }) => {
                                    if (value !== fieldApi.form.getFieldValue("newPassword")) {
                                        return "Passwords do not match";
                                    }
                                    return undefined;
                                },
                            }}
                        >
                            {(field) => (
                                <PasswordField
                                    label={"Confirm New Password"}
                                    value={field.state.value}
                                    onChange={field.handleChange}
                                    required
                                    error={field.state.meta.errors.length > 0}
                                    helperText={field.state.meta.errors.join(Delimiter)}
                                />
                            )}
                        </form.Field>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button variant={"outlined"} onClick={onClose}>
                        Cancel
                    </Button>
                    <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
                        {([canSubmit]) => (
                            <Button
                                variant={"contained"}
                                type={"submit"}
                                disabled={!canSubmit || adminChangePassword.isPending}
                            >
                                Save
                            </Button>
                        )}
                    </form.Subscribe>
                </DialogActions>
            </form>
        </Dialog>
    );
}
